import { create } from 'zustand';

// ── Types ──

export type ToastType = 'success' | 'error' | 'warning' | 'info' | 'alert';

export type NotificationCategory = 'alerts' | 'system' | 'investigations' | 'reports';

export interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message: string;
  duration?: number; // ms, default 5000
  action?: {
    label: string;
    onClick: () => void;
  };
  createdAt: number;
}

export interface Notification {
  id: string;
  type: ToastType;
  category: NotificationCategory;
  title: string;
  message: string;
  read: boolean;
  timestamp: string;
  link?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface NotificationStore {
  // State
  notifications: Notification[];
  toasts: Toast[];
  toastQueue: Toast[];
  unreadCount: number;
  panelOpen: boolean;
  filterCategory: NotificationCategory | 'all';

  // Toast actions
  addToast: (toast: Omit<Toast, 'id' | 'createdAt'>) => void;
  removeToast: (id: string) => void;
  promoteFromQueue: () => void;

  // Notification actions
  addNotification: (notification: Omit<Notification, 'id' | 'read' | 'timestamp'>) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  clearAll: () => void;
  removeNotification: (id: string) => void;

  // Panel actions
  setPanelOpen: (open: boolean) => void;
  togglePanel: () => void;
  setFilterCategory: (category: NotificationCategory | 'all') => void;
}

const MAX_VISIBLE_TOASTS = 5;
const MAX_NOTIFICATIONS = 200;

export const useNotificationStore = create<NotificationStore>((set, _get) => ({
  notifications: [],
  toasts: [],
  toastQueue: [],
  unreadCount: 0,
  panelOpen: false,
  filterCategory: 'all',

  addToast: (toast) => {
    const newToast: Toast = {
      ...toast,
      id: crypto.randomUUID(),
      createdAt: Date.now(),
    };

    set((state) => {
      if (state.toasts.length >= MAX_VISIBLE_TOASTS) {
        return {
          toastQueue: [...state.toastQueue, newToast],
        };
      }
      return {
        toasts: [...state.toasts, newToast],
      };
    });
  },

  removeToast: (id) => {
    set((state) => {
      const filtered = state.toasts.filter((t) => t.id !== id);
      // Promote from queue if space available
      if (filtered.length < MAX_VISIBLE_TOASTS && state.toastQueue.length > 0) {
        const [next, ...rest] = state.toastQueue;
        return {
          toasts: [...filtered, next],
          toastQueue: rest,
        };
      }
      return { toasts: filtered };
    });
  },

  promoteFromQueue: () => {
    set((state) => {
      if (state.toasts.length < MAX_VISIBLE_TOASTS && state.toastQueue.length > 0) {
        const [next, ...rest] = state.toastQueue;
        return {
          toasts: [...state.toasts, next],
          toastQueue: rest,
        };
      }
      return state;
    });
  },

  addNotification: (notification) => {
    const newNotif: Notification = {
      ...notification,
      id: crypto.randomUUID(),
      read: false,
      timestamp: new Date().toISOString(),
    };
    set((state) => ({
      notifications: [newNotif, ...state.notifications].slice(0, MAX_NOTIFICATIONS),
      unreadCount: state.unreadCount + 1,
    }));
  },

  markAsRead: (id) => {
    set((state) => {
      const notif = state.notifications.find((n) => n.id === id);
      if (!notif || notif.read) return state;
      return {
        notifications: state.notifications.map((n) =>
          n.id === id ? { ...n, read: true } : n
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
      };
    });
  },

  markAllAsRead: () => {
    set((state) => ({
      notifications: state.notifications.map((n) => ({ ...n, read: true })),
      unreadCount: 0,
    }));
  },

  clearAll: () => {
    set({ notifications: [], unreadCount: 0 });
  },

  removeNotification: (id) => {
    set((state) => {
      const notif = state.notifications.find((n) => n.id === id);
      return {
        notifications: state.notifications.filter((n) => n.id !== id),
        unreadCount: notif && !notif.read ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
      };
    });
  },

  setPanelOpen: (open) => set({ panelOpen: open }),
  togglePanel: () => set((state) => ({ panelOpen: !state.panelOpen })),
  setFilterCategory: (category) => set({ filterCategory: category }),
}));
